import type { ReactElement, ReactNode } from 'react';

import { Box, type BoxProps } from '@bonusly/design-system';

type LockedSurfaceStyle = 'position' | 'zIndex';

export interface BizyCoveredSurfaceProps extends Omit<BoxProps, LockedSurfaceStyle | 'children'> {
	/** True while an overlay (e.g. the person view) sits on top of this surface. */
	isCovered: boolean;
	children: ReactNode;
}

/**
 * Host for a surface that stays mounted while an overlay covers it.
 *
 * `zIndex` is what keeps the overlay on top: it makes this a stacking context,
 * so a descendant that raises itself competes with its siblings here rather than
 * with the overlay (BIZY-786). `inert` keeps the covered controls out of the tab
 * order and the a11y tree, with `aria-hidden` as the fallback where `inert` is
 * unsupported — safe because the overlay takes focus on mount, so it never
 * aria-hides a subtree holding focus.
 */
export function BizyCoveredSurface({ isCovered, children, ...props }: BizyCoveredSurfaceProps): ReactElement {
	// Untyped in React 18, so it goes through as an attribute; becomes
	// `inert={isCovered}` on the React 19 upgrade.
	const inertProps = isCovered ? ({ inert: '' } as Record<string, string>) : {};

	return (
		<Box {...props} position="relative" zIndex={0} aria-hidden={isCovered || undefined} {...inertProps} data-component="bizy-covered-surface">
			{children}
		</Box>
	);
}
